// src/utils/pricingCalculator.js
import distanceCalculator from './distanceCalculator';

export const PRICING = {
  BASE_RATE_PER_MILE: 1.40,
  MINIMUM_CHARGES: {
    LOCAL: 60,      // Hampshire jobs
    UK_WIDE: 120,   // UK-wide jobs
  },
  URGENCY_SURCHARGES: {
    STANDARD: 0.10,     // +10%
    SAME_DAY: 0.20,     // +20%
    OVERNIGHT: 0.30,    // +30%
    IMMEDIATE: 0.40,    // +40%
  },
  TIME_SURCHARGES: {
    EVENING: 0.10,      // 6pm-10pm
    NIGHT: 0.25,        // 10pm-6am
    WEEKEND: 0.30,
    BANK_HOLIDAY: 0.50,
  },
  LOAD_MULTIPLIERS: {
    SMALL_PARCELS: 1.00,
    ONE_PALLET: 1.20,
    TWO_THREE_PALLETS: 1.30,
    FOUR_PALLETS: 1.50,
  },
  EXTRAS: {
    WAITING_TIME_PER_HOUR: 20,
    INSURANCE_RATE: 0.01, // 1% of value over £1000
  }
};

// Hampshire towns/cities for validation
export const HAMPSHIRE_TOWNS = [
  'Alton', 'Andover', 'Basingstoke', 'Eastleigh', 'Fareham', 'Farnborough', 'Fleet',
  'Gosport', 'Havant', 'Lymington', 'New Milton', 'Petersfield', 'Portsmouth',
  'Ringwood', 'Romsey', 'Southampton', 'Waterlooville', 'Winchester', 'Yateley'
];

// Hampshire postcode areas for validation
export const HAMPSHIRE_POSTCODE_AREAS = [
  'SO', 'PO', 'RG21', 'RG22', 'RG23', 'RG24', 'RG25', 'RG26', 'RG27', 'RG28', 'RG29',
  'GU9', 'GU10', 'GU11', 'GU12', 'GU13', 'GU14', 'GU15', 'GU16', 'GU17', 'GU30', 'GU31', 'GU32', 'GU33', 'GU34', 'GU35',
  'SP5', 'SP9', 'SP10', 'SP11'
];

// Validate if pickup is in Hampshire
export const isHampshirePostcode = (postcode) => {
  if (!postcode) return false;
  const cleaned = postcode.replace(/\s+/g, '').toUpperCase();
  const area = cleaned.match(/^[A-Z]{1,2}[0-9]/)?.[0].replace(/[0-9]/g, '');
  const fullArea = cleaned.match(/^[A-Z]{1,2}[0-9][A-Z0-9]?/)?.[0];
  
  // Check specific areas
  if (HAMPSHIRE_POSTCODE_AREAS.includes(fullArea)) return true;
  if (area && ['SO', 'PO', 'RG', 'GU', 'SP'].includes(area)) {
    // Check specific RG areas (only some are Hampshire)
    if (area === 'RG') {
      const rgNumber = cleaned.match(/^RG([0-9]+)/)?.[1];
      const rgNum = parseInt(rgNumber);
      return rgNum >= 21 && rgNum <= 29;
    }
    // Check specific GU areas
    if (area === 'GU') {
      const guNumber = cleaned.match(/^GU([0-9]+)/)?.[1];
      const guNum = parseInt(guNumber);
      return (guNum >= 9 && guNum <= 17) || (guNum >= 30 && guNum <= 35);
    }
    // Check specific SP areas
    if (area === 'SP') {
      const spNumber = cleaned.match(/^SP([0-9]+)/)?.[1];
      const spNum = parseInt(spNumber);
      return spNum === 5 || spNum === 9 || spNum === 10 || spNum === 11;
    }
    return true;
  }
  return false;
};

// Format percentage with proper rounding
const formatPercentage = (value) => {
  return Math.round(value * 100) / 100; // Round to 2 decimal places
};

// Main pricing calculation function
export const calculatePrice = async (bookingData) => {
  const {
    fromPostcode,
    toPostcode,
    urgency,
    timeOfDay,
    isWeekend,
    isBankHoliday,
    loadType,
    waitingHours = 0,
    declaredValue = 0,
    numberOfPallets = 0,
  } = bookingData;

  // Validate pickup is in Hampshire
  if (!isHampshirePostcode(fromPostcode)) {
    throw new Error('Pickup must be in Hampshire. We currently only service Hampshire locations.');
  }

  // Step 1: Calculate distance
  const oneWayMiles = await distanceCalculator.calculateDistance(fromPostcode, toPostcode);
  const roundTripMiles = oneWayMiles * 2;

  // Step 2: Base rate calculation
  let basePrice = roundTripMiles * PRICING.BASE_RATE_PER_MILE;

  // Step 3: Check minimum charge
  const isLocal = isHampshirePostcode(toPostcode);
  const minimumCharge = isLocal ? PRICING.MINIMUM_CHARGES.LOCAL : PRICING.MINIMUM_CHARGES.UK_WIDE;
  if (basePrice < minimumCharge) {
    basePrice = minimumCharge;
  }

  // Step 4: Apply urgency surcharge
  let urgencyMultiplier = 1;
  switch (urgency) {
    case 'standard':
      urgencyMultiplier += PRICING.URGENCY_SURCHARGES.STANDARD;
      break;
    case 'same_day':
      urgencyMultiplier += PRICING.URGENCY_SURCHARGES.SAME_DAY;
      break;
    case 'overnight':
      urgencyMultiplier += PRICING.URGENCY_SURCHARGES.OVERNIGHT;
      break;
    case 'immediate':
      urgencyMultiplier += PRICING.URGENCY_SURCHARGES.IMMEDIATE;
      break;
    default:
      urgencyMultiplier = 1;
  }

  // Step 5: Apply time surcharges
  let timeMultiplier = 1;
  if (isBankHoliday) {
    timeMultiplier += PRICING.TIME_SURCHARGES.BANK_HOLIDAY;
  } else if (isWeekend) {
    timeMultiplier += PRICING.TIME_SURCHARGES.WEEKEND;
  } else if (timeOfDay === 'evening') {
    timeMultiplier += PRICING.TIME_SURCHARGES.EVENING;
  } else if (timeOfDay === 'night') {
    timeMultiplier += PRICING.TIME_SURCHARGES.NIGHT;
  }

  // Step 6: Apply load multiplier
  let loadMultiplier = PRICING.LOAD_MULTIPLIERS.SMALL_PARCELS;
  if (loadType === 'pallet') {
    if (numberOfPallets === 1) {
      loadMultiplier = PRICING.LOAD_MULTIPLIERS.ONE_PALLET;
    } else if (numberOfPallets >= 2 && numberOfPallets <= 3) {
      loadMultiplier = PRICING.LOAD_MULTIPLIERS.TWO_THREE_PALLETS;
    } else if (numberOfPallets >= 4) {
      loadMultiplier = PRICING.LOAD_MULTIPLIERS.FOUR_PALLETS;
    }
  }

  // Calculate adjusted price
  let adjustedPrice = basePrice * urgencyMultiplier * timeMultiplier * loadMultiplier;

  // Step 7: Add extras
  let extrasTotal = 0;
  
  // Waiting time (first 10 minutes free)
  const effectiveWaitingHours = Math.max(0, waitingHours - (10/60));
  if (effectiveWaitingHours > 0) {
    extrasTotal += effectiveWaitingHours * PRICING.EXTRAS.WAITING_TIME_PER_HOUR;
  }

  // Insurance (1% of value over £1000)
  if (declaredValue > 1000) {
    const insuranceCost = (declaredValue - 1000) * PRICING.EXTRAS.INSURANCE_RATE;
    extrasTotal += insuranceCost;
  }

  // Final price
  const finalPrice = adjustedPrice + extrasTotal;

  // Calculate breakdown with proper rounding
  const urgencyPercent = formatPercentage((urgencyMultiplier - 1) * 100);
  const timePercent = formatPercentage((timeMultiplier - 1) * 100);
  const loadPercent = formatPercentage((loadMultiplier - 1) * 100);

  // Return breakdown
  return {
    basePrice: Math.round(basePrice),
    roundTripMiles: Math.round(roundTripMiles),
    oneWayMiles: Math.round(oneWayMiles),
    urgencyMultiplier: urgencyPercent,
    timeMultiplier: timePercent,
    loadMultiplier: loadPercent,
    extrasTotal: Math.round(extrasTotal),
    finalPrice: Math.ceil(finalPrice),
    isLocal,
    breakdown: {
      mileageCost: Math.round(basePrice),
      urgencySurcharge: Math.round(basePrice * (urgencyMultiplier - 1)),
      timeSurcharge: Math.round(basePrice * urgencyMultiplier * (timeMultiplier - 1)),
      loadSurcharge: Math.round(basePrice * urgencyMultiplier * timeMultiplier * (loadMultiplier - 1)),
      extras: Math.round(extrasTotal),
    }
  };
};

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};